import { useState, useEffect } from "react";
import { CreditCard, Save, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

const SubscriptionDashboard = () => {
  const [subscriptionSettings, setSubscriptionSettings] = useState({
    monthlyRate: 5,
    paymentMethod: 'razorpay',
    subscriptionOptions: [
      { id: 'monthly', label: 'Monthly', months: 1, enabled: false, discount: 0 },
      { id: '3month', label: '3 Months', months: 3, enabled: false, discount: 0 },
      { id: '6month', label: '6 Months', months: 6, enabled: true, discount: 100 },
      { id: 'yearly', label: 'Yearly', months: 12, enabled: false, discount: 0 },
      { id: '3yearly', label: '3 Years', months: 36, enabled: false, discount: 0 }
    ]
  });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('adminSubscriptionSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSubscriptionSettings(parsed);
      } catch (e) {
        console.error('Error loading subscription settings:', e);
      }
    }
  }, []);

  // Broadcast changes to all users
  const broadcastSettings = () => {
    // Store in localStorage
    localStorage.setItem('adminSubscriptionSettings', JSON.stringify(subscriptionSettings));
    
    // Dispatch custom event for real-time updates
    window.dispatchEvent(new CustomEvent('subscriptionSettingsChanged', {
      detail: subscriptionSettings
    }));
    
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleSave = () => {
    try {
      broadcastSettings();
      setError(null);
    } catch (e) {
      setError('Failed to save settings. Please try again.');
      console.error('Error saving settings:', e);
    }
  };

  const handleMonthlyRateChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setSubscriptionSettings(prev => ({
      ...prev,
      monthlyRate: value
    }));
  };

  const handlePaymentMethodChange = (e) => {
    setSubscriptionSettings(prev => ({
      ...prev,
      paymentMethod: e.target.value
    }));
  };

  const handleOptionChange = (optionId, field, value) => {
    setSubscriptionSettings(prev => ({
      ...prev,
      subscriptionOptions: prev.subscriptionOptions.map(opt =>
        opt.id === optionId ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const calculatePrice = (months) => {
    return months * subscriptionSettings.monthlyRate;
  };

  const calculateFinalPrice = (option) => {
    const originalPrice = calculatePrice(option.months);
    const discountAmount = (originalPrice * option.discount) / 100;
    return originalPrice - discountAmount;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-red-700 dark:from-red-400 dark:to-red-500 bg-clip-text text-transparent">
            Subscription Management
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage subscription plans, pricing, and payment methods
          </p>
        </div>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-4 py-2 backdrop-blur-sm bg-gradient-to-r from-red-500/80 to-red-600/80 dark:from-red-600/80 dark:to-red-700/80 text-white rounded-xl hover:from-red-600 hover:to-red-700 dark:hover:from-red-700 dark:hover:to-red-800 transition-all duration-300 shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40"
        >
          {saved ? (
            <>
              <CheckCircle2 className="w-5 h-5" />
              <span>Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="p-4 backdrop-blur-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Monthly Rate Setting */}
      <div className="backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl p-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Monthly Subscription Rate (Ôé╣)
        </label>
        <div className="flex items-center gap-4">
          <input
            type="number"
            min="0"
            step="0.01"
            value={subscriptionSettings.monthlyRate}
            onChange={handleMonthlyRateChange}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 w-32"
          />
          <span className="text-gray-600 dark:text-gray-400 text-sm">
            per month
          </span>
        </div>
      </div>

      {/* Payment Method */}
      <div className="backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl p-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <CreditCard className="w-4 h-4" />
          Payment Method
        </label>
        <select
          value={subscriptionSettings.paymentMethod}
          onChange={handlePaymentMethodChange}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 w-full max-w-xs"
        >
          <option value="razorpay">Razorpay</option>
          <option value="stripe">Stripe</option>
          <option value="paypal">PayPal</option>
          <option value="cashfree">Cashfree</option>
          <option value="manual">Manual Payment</option>
        </select>
      </div>

      {/* Subscription Options */}
      <div className="backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Subscription Plans
        </h3>
        <div className="space-y-4">
          {subscriptionSettings.subscriptionOptions.map((option) => {
            const originalPrice = calculatePrice(option.months);
            const finalPrice = calculateFinalPrice(option);
            
            return (
              <div
                key={option.id}
                className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                  option.enabled
                    ? 'border-red-500 dark:border-red-400 bg-red-50/50 dark:bg-red-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white/30 dark:bg-gray-800/30'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        type="checkbox"
                        checked={option.enabled}
                        onChange={(e) => handleOptionChange(option.id, 'enabled', e.target.checked)}
                        className="w-5 h-5 text-red-600 dark:text-red-400 rounded focus:ring-red-500 dark:focus:ring-red-400"
                      />
                      <label className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        {option.label}
                      </label>
                      {option.enabled && (
                        <span className="px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 ml-8">
                      {option.months === 1 ? '1 month' : `${option.months} months`} subscription
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-800 dark:text-gray-200">
                      Ôé╣{originalPrice}
                    </div>
                    {option.discount > 0 && (
                      <div className="text-sm text-green-600 dark:text-green-400">
                        {option.discount}% OFF
                      </div>
                    )}
                  </div>
                </div>

                <div className="ml-8 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={option.discount}
                      onChange={(e) => handleOptionChange(option.id, 'discount', parseFloat(e.target.value) || 0)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 w-32"
                      disabled={!option.enabled}
                    />
                  </div>
                  
                  {option.enabled && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600 dark:text-gray-400">Original Price:</span>
                        <span className="font-semibold text-gray-800 dark:text-gray-200">Ôé╣{originalPrice}</span>
                      </div>
                      {option.discount > 0 && (
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600 dark:text-gray-400">Discount ({option.discount}%):</span>
                          <span className="font-semibold text-green-600 dark:text-green-400">
                            -Ôé╣{((originalPrice * option.discount) / 100).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-blue-200 dark:border-blue-800">
                        <span className="text-gray-800 dark:text-gray-200">Final Price:</span>
                        <span className="text-red-600 dark:text-red-400">Ôé╣{finalPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 backdrop-blur-sm bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <div className="flex items-start gap-3">
          <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-1">Changes take effect immediately</p>
            <p>When you save changes, all users will see the updated subscription plans and prices in real-time.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDashboard;







